import { useState, useEffect, useRef, useCallback } from 'react';
import { baseUrl } from '../pages/dasboard';
import { useSSEContext } from './SSEContext';

/**
 * Custom hook for SSE connection to table updates
 * @param {string} tableId - The table ID to subscribe to
 * @param {Object} options - Options for the hook
 * @param {boolean} options.enabled - Whether the SSE connection should be active (default: true)
 * @returns {Object} - { tableData, isConnected, error, reconnect }
 */
export function useTableSSE(tableId, options = {}) {
  const { enabled = true } = options;
  const { setConnectionStatus } = useSSEContext();
  
  const [tableData, setTableData] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState(null);
  
  const eventSourceRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;
  const baseReconnectDelay = 1000; // 1 second
  
  const connect = useCallback(() => {
    if (!tableId || !enabled) {
      return;
    }
    
    // Close existing connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }
    
    const url = `${baseUrl}/tables/${tableId}/stream`;
    
    try {
      const eventSource = new EventSource(url);
      eventSourceRef.current = eventSource;
      
      eventSource.onopen = () => {
        setIsConnected(true);
        setConnectionStatus(true, tableId);
        setError(null);
        reconnectAttempts.current = 0;
        console.log('[SSE] Connected to table stream:', tableId);
      };
      
      eventSource.onmessage = (event) => {
        console.log('[SSE] Raw event received:', event.data);
        try {
          const data = JSON.parse(event.data);
          console.log('[SSE] Parsed data:', data);
          console.log('[SSE] data.type:', data.type, 'data.data:', data.data);
          
          if (data.type === 'initial' || data.type === 'table_update') {
            const table = data.data;
            console.log('[SSE] Processing table data:', table);
            
            // Convert players object/array to array format expected by the table component
            if (table && table.players) {
              const players = Array.isArray(table.players) 
                ? table.players 
                : Object.values(table.players);
              
              console.log('[SSE] Players array:', players);
              
              const convertedData = players.map((player) => ({
                ...player,
                key: player.id,
                // Map backend field names to frontend expected names
                chips: player.points !== undefined ? player.points : player.chips,
              }));
              
              console.log('[SSE] Converted data to set:', convertedData);
              setTableData(convertedData);
            } else {
              console.log('[SSE] No table or no players in table:', table);
            }
            
            if (data.event_type) {
              console.log('[SSE] Table update event_type:', data.event_type);
            }
          } else {
            console.log('[SSE] Unhandled message type:', data.type);
          }
        } catch (parseError) {
          console.error('[SSE] Error parsing message:', parseError, 'Raw:', event.data);
        }
      };
      
      eventSource.onerror = (event) => {
        console.error('[SSE] Connection error:', event);
        setIsConnected(false);
        setConnectionStatus(false, tableId);
        eventSource.close();
        
        // Attempt reconnection with exponential backoff
        if (reconnectAttempts.current < maxReconnectAttempts) {
          const delay = baseReconnectDelay * Math.pow(2, reconnectAttempts.current);
          reconnectAttempts.current += 1;
          
          console.log(`[SSE] Reconnecting in ${delay}ms (attempt ${reconnectAttempts.current}/${maxReconnectAttempts})`);
          
          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, delay);
        } else {
          setError('Failed to connect after multiple attempts');
          console.error('[SSE] Max reconnection attempts reached');
        }
      };
    } catch (err) {
      console.error('[SSE] Failed to create EventSource:', err);
      setError(err.message);
    }
  }, [tableId, enabled, setConnectionStatus]);
  
  // Manual reconnect function
  const reconnect = useCallback(() => {
    reconnectAttempts.current = 0;
    setError(null);
    connect();
  }, [connect]);
  
  // Connect when tableId changes or enabled changes
  useEffect(() => {
    if (enabled && tableId) {
      connect();
    }
    
    return () => {
      // Cleanup on unmount or when dependencies change
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      setConnectionStatus(false, null);
    };
  }, [tableId, enabled, connect]);
  
  return {
    tableData,
    isConnected,
    error,
    reconnect,
  };
}

export default useTableSSE;
